/**
 * Catalog of clause-edit actions used by the Redlining tab and the analysis
 * service's prompt. Adding an entry here makes it available to the backend.
 *
 * `gerund` is the present-participle label shown during loading (e.g. the
 * spinner says "Shortening…" when editType.id === "shorten"). If omitted,
 * the UI falls back to "Working…".
 */

export type EditCategory =
  | 'Length & clarity'
  | 'Strictness & balance'
  | 'Precision'
  | 'Negotiation'
  | 'Structure'
  | 'Risk & compliance';

export interface EditType {
  id: string;
  label: string;
  description: string;
  category: EditCategory;
  analysisOnly?: boolean;
  /** Present participle shown in loading state ("Shortening", "Simplifying"…). */
  gerund?: string;
}

export const EDIT_TYPES: EditType[] = [
  {
    id: 'shorten',
    label: 'Shorten',
    description: 'Compress without losing legal effect.',
    category: 'Length & clarity',
    gerund: 'Shortening',
  },
  {
    id: 'simplify',
    label: 'Simplify',
    description: 'Plain English; strip legalese.',
    category: 'Length & clarity',
    gerund: 'Simplifying',
  },
  {
    id: 'modernize',
    label: 'Modernize',
    description: 'Update archaic phrasing while preserving meaning.',
    category: 'Length & clarity',
    gerund: 'Modernizing',
  },
  {
    id: 'stricter',
    label: 'Make stricter',
    description: 'Tighten obligations on the counterparty.',
    category: 'Strictness & balance',
    gerund: 'Tightening',
  },
  {
    id: 'more_lenient',
    label: 'Make more lenient',
    description: 'Soften obligations (useful when client is the bound party).',
    category: 'Strictness & balance',
    gerund: 'Easing',
  },
  {
    id: 'mutualize',
    label: 'Mutualize',
    description: 'Convert one-sided language to reciprocal.',
    category: 'Strictness & balance',
    gerund: 'Mutualizing',
  },
  {
    id: 'favor_my_side',
    label: 'Favor my side',
    description: 'Shift risk and obligation toward counterparty.',
    category: 'Strictness & balance',
    gerund: 'Favoring your side',
  },
  {
    id: 'neutralize',
    label: 'Neutralize',
    description: 'Make the clause balanced and market-standard — strip one-sided edges.',
    category: 'Strictness & balance',
    gerund: 'Neutralizing',
  },
  {
    id: 'add_specificity',
    label: 'Add specificity',
    description: 'Replace vague terms with concrete numbers, dates, defined terms.',
    category: 'Precision',
    gerund: 'Adding specificity',
  },
  {
    id: 'remove_ambiguity',
    label: 'Remove ambiguity',
    description: 'Close loopholes; eliminate undefined terms.',
    category: 'Precision',
    gerund: 'Tightening',
  },
  {
    id: 'soften_tone',
    label: 'Soften',
    description: 'Friendlier phrasing for early-draft negotiation.',
    category: 'Negotiation',
    gerund: 'Softening',
  },
  {
    id: 'aggressive',
    label: 'Aggressive',
    description: 'Maximally favorable phrasing.',
    category: 'Negotiation',
    gerund: 'Sharpening',
  },
  {
    id: 'neutral',
    label: 'Neutral',
    description: 'Market-standard balance.',
    category: 'Negotiation',
    gerund: 'Balancing',
  },
  {
    id: 'conservative',
    label: 'Conservative',
    description: 'Counterparty-friendly fallback.',
    category: 'Negotiation',
    gerund: 'Adding safeguards',
  },
  {
    id: 'restructure',
    label: 'Restructure',
    description: 'Break into numbered subsections.',
    category: 'Structure',
    gerund: 'Restructuring',
  },
  {
    id: 'extract_definitions',
    label: 'Extract definitions',
    description: 'Pull defined terms into a definitions section.',
    category: 'Structure',
    gerund: 'Extracting definitions',
  },
  {
    id: 'identify_risks',
    label: 'Identify risks',
    description: 'Flag risks (no rewrite).',
    category: 'Risk & compliance',
    analysisOnly: true,
    gerund: 'Identifying risks',
  },
  {
    id: 'add_carveouts',
    label: 'Add carve-outs',
    description: 'Insert standard exceptions.',
    category: 'Risk & compliance',
    gerund: 'Adding carve-outs',
  },
  {
    id: 'add_cure_period',
    label: 'Add cure period',
    description: 'For breach / termination clauses.',
    category: 'Risk & compliance',
    gerund: 'Adding cure period',
  },
  {
    id: 'jurisdiction_adapt',
    label: 'Jurisdiction-adapt',
    description: 'Rewrite for a specific governing law.',
    category: 'Risk & compliance',
    gerund: 'Adapting to jurisdiction',
  },
  {
    id: 'compliance_check',
    label: 'Compliance check',
    description: 'Flag against GDPR, CCPA, HIPAA, etc.',
    category: 'Risk & compliance',
    analysisOnly: true,
    gerund: 'Checking compliance',
  },
];

export const REDLINING_EDIT_TYPE_IDS: string[] = [
  'shorten',
  'simplify',
  'soften_tone',
  'add_specificity',
  'favor_my_side',
  'neutralize',
];

export function getEditType(id: string): EditType | undefined {
  return EDIT_TYPES.find(e => e.id === id);
}

export function getRedliningEditTypes(): EditType[] {
  return REDLINING_EDIT_TYPE_IDS.map(id => getEditType(id)).filter(
    (e): e is EditType => e !== undefined,
  );
}

export function gerundFor(editType: EditType | null | undefined): string {
  if (!editType) return 'Working';
  if (editType.gerund) return editType.gerund;
  if (editType.id === 'custom') return 'Generating';
  return 'Working';
}
